/**
 * step-user Component - User-Focused View of a Single Step
 *
 * Renders a single step in USER mode showing:
 * - User message
 * - Tool call explanations (LLM's reasoning)
 * - Thinking spinner when waiting for responses
 * - Expandable "Show details" for raw tool call data
 *
 * Memoized to prevent re-renders when props haven't changed
 */

import React from 'react'
import { Step } from '@sourcepilot/generated/chat/api/step';
import { ChatRoleType } from '@sourcepilot/generated/shared/enums';
import { UserMessage } from '@sourcepilot/ui/framework/ui/atoms/user-message'
import { StepRequestsUser } from './StepRequestsUser'
import { ThinkingSpinner } from '@sourcepilot/ui/framework/ui/atoms/thinking-spinner'

interface StepUserProps {
  step: Step;
  isLastStep?: boolean;
}

export const StepUser = React.memo(function StepUser({
  step,
  isLastStep = false
}: StepUserProps) {
  // Show user message with explanation-focused tool calls
  if (step.role === ChatRoleType.USER) {
    return (
      <>
        <UserMessage message={step} />

        {/* Tool call explanations and results (user-focused view) */}
        <StepRequestsUser step={step} isLastStep={isLastStep} />
      </>
    );
  }

  // Other step roles can be added here as needed
  return null;
}, (prevProps, nextProps) => {
  // Custom comparison: re-render if step.id, created_at changes, or when transitioning from pending
  // Also re-render when id changes from local- to server UUID
  const prevIsPending = !prevProps.step.id || prevProps.step.id.startsWith('local-');
  const nextIsPending = !nextProps.step.id || nextProps.step.id.startsWith('local-');
  return prevProps.step.id === nextProps.step.id &&
         prevProps.step.created_at === nextProps.step.created_at &&
         prevProps.isLastStep === nextProps.isLastStep &&
         prevIsPending === nextIsPending;
});

StepUser.displayName = 'StepUser';
